<template>
  <div class="purchase-cost">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Supplier</label>
          <FormDropdown v-model="filters.supplier" :options="supplierOptions" placeholder="All suppliers" clearable />
        </div>
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search item, code or SKU" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-stack-2"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Products · Batches</div>
          <div class="rp-card-value">{{ formatInt(summary.product_count) }}</div>
          <div class="rp-card-sub">{{ plural(summary.batch_count, 'batch') }} · {{ formatQty(summary.qty) }} units</div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-tag"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Average Unit Cost</div>
          <div class="rp-card-value">{{ formatMoney(summary.avg_cost) }}</div>
          <div class="rp-card-sub">on {{ formatMoney(summary.cost) }} purchased</div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: filters.show === 'up' }"
              :title="filters.show === 'up' ? 'Show all products' : 'Show products whose cost rose'" @click="toggleShow('up')">
        <span class="rp-card-icon"><i class="ti ti-trending-up"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Rising · Falling</div>
          <div class="rp-card-value">
            <span class="text-danger">{{ formatInt(summary.rising_count) }}</span>
            <span class="text-muted fw-normal"> · </span>
            <span class="text-success">{{ formatInt(summary.falling_count) }}</span>
          </div>
          <div class="rp-card-sub">products, latest batch vs first batch</div>
        </div>
      </button>
      <button type="button" class="rp-card rp-card-action tone-green" :class="{ selected: filters.show === 'multi_supplier' }"
              :title="filters.show === 'multi_supplier' ? 'Show all products' : 'Show products bought from several suppliers'"
              @click="toggleShow('multi_supplier')">
        <span class="rp-card-icon"><i class="ti ti-pig-money"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Possible Saving</div>
          <div class="rp-card-value">{{ formatMoney(summary.saving) }}</div>
          <div class="rp-card-sub">buying everything at the cheapest supplier's price</div>
        </div>
      </button>
    </div>

    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Biggest cost rises</div>
        <ul v-if="summary.biggest_rises.length" class="rp-ranked">
          <li v-for="(item, index) in summary.biggest_rises" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatMoney(item.first_cost) }} → {{ formatMoney(item.last_cost) }}</small>
            </div>
            <span class="rp-badge danger"><i class="ti ti-arrow-up-right"></i>{{ item.change_pct }}%</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No product got dearer in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Biggest cost drops</div>
        <ul v-if="summary.biggest_drops.length" class="rp-ranked">
          <li v-for="(item, index) in summary.biggest_drops" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatMoney(item.first_cost) }} → {{ formatMoney(item.last_cost) }}</small>
            </div>
            <span class="rp-badge success"><i class="ti ti-arrow-down-right"></i>{{ Math.abs(item.change_pct) }}%</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No product got cheaper in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between">
          <span>Supplier price index</span>
          <small class="text-muted fw-normal">100 = average price</small>
        </div>
        <div v-if="summary.supplier_index.length" class="rp-bars">
          <div v-for="supplier in indexBars" :key="supplier.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2" :title="supplier.name">
                {{ supplier.name }}
                <span class="text-muted">· {{ plural(supplier.items, 'item') }}</span>
              </span>
              <span class="fw-semibold text-nowrap" :class="indexClass(supplier.index)">
                {{ supplier.index === null ? '—' : supplier.index }}
              </span>
            </div>
            <div class="rp-bar-track">
              <div class="rp-bar-fill" :class="indexTone(supplier.index)" :style="{ width: `${supplier.percent}%` }"></div>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No purchases in this period.</p>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in showPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.show || 'all') === pill.key }" @click="setShow(pill.key)">
            <i v-if="pill.icon" class="ti" :class="pill.icon"></i>
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button type="button" class="rp-expand" :title="expanded[row.key] ? 'Hide batches' : 'Show batches and suppliers'"
                          @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td>
                  <div class="fw-medium">{{ row.item_name }}</div>
                  <span class="rp-sub">{{ row.item_code }}<template v-if="row.category"> · {{ row.category }}</template></span>
                  <span v-for="part in variantParts(row)" :key="part" class="rp-variant mt-1">{{ part }}</span>
                </td>
                <td class="text-center">
                  <span class="rp-count">{{ row.batch_count }}</span>
                  <span class="rp-sub">{{ plural(row.supplier_count, 'supplier') }}</span>
                </td>
                <td class="text-end">
                  {{ formatMoney(row.first_cost) }}
                  <span class="rp-sub">{{ formatDate(row.first_date) }}</span>
                </td>
                <td class="text-end fw-semibold">
                  {{ formatMoney(row.last_cost) }}
                  <span class="rp-sub">{{ formatDate(row.last_date) }}</span>
                </td>
                <td class="text-end">
                  <span class="rp-badge" :class="TREND_TONES[row.trend]">
                    <i class="ti" :class="TREND_ICONS[row.trend]"></i>
                    {{ row.trend === 'up' || row.trend === 'down' ? `${Math.abs(row.change_pct)}%` : row.trend_label }}
                  </span>
                  <span v-if="Number(row.change)" class="rp-sub">{{ signedMoney(row.change) }}</span>
                </td>
                <td class="text-end text-nowrap">
                  <template v-if="row.min_cost === row.max_cost">{{ formatMoney(row.min_cost) }}</template>
                  <template v-else>
                    {{ formatMoney(row.min_cost) }} – {{ formatMoney(row.max_cost) }}
                    <span class="rp-sub">{{ row.spread_pct }}% apart</span>
                  </template>
                </td>
                <td class="text-end">{{ formatMoney(row.avg_cost) }}</td>
                <td class="text-end">
                  <template v-if="Number(row.purchase_price)">
                    {{ formatMoney(row.purchase_price) }}
                    <span v-if="row.price_gap !== null && Math.abs(Number(row.price_gap)) >= 0.005" class="rp-sub"
                          :class="Number(row.price_gap) > 0 ? 'text-danger' : 'text-success'">
                      last {{ signedMoney(row.price_gap) }}
                    </span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end">
                  <template v-if="row.best_supplier">
                    <div class="text-truncate pc-best" :title="row.best_supplier">{{ row.best_supplier }}</div>
                    <span v-if="Number(row.saving)" class="rp-sub text-success">save {{ formatMoney(row.saving) }}</span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="pc-detail">
                      <div class="min-w-0">
                        <div class="rp-detail-title"><i class="ti ti-stack-2 me-1"></i>Batches (newest first)</div>
                        <div class="table-responsive">
                          <table class="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Invoice</th>
                                <th>Batch</th>
                                <th>Supplier</th>
                                <th class="text-end">Qty</th>
                                <th class="text-end">Unit Cost</th>
                                <th class="text-end">vs Avg</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(batch, index) in row.batches" :key="`${batch.invoice_no}-${batch.batch_no}-${index}`">
                                <td class="text-nowrap">{{ formatDate(batch.date) }}</td>
                                <td>{{ batch.invoice_no }}</td>
                                <td>{{ batch.batch_no || '—' }}</td>
                                <td>{{ batch.supplier }}</td>
                                <td class="text-end">{{ formatQty(batch.qty) }}</td>
                                <td class="text-end fw-semibold">{{ formatMoney(batch.unit_cost) }}</td>
                                <td class="text-end" :class="diffClass(batch.unit_cost, row.avg_cost)">
                                  {{ diffText(batch.unit_cost, row.avg_cost) }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div class="min-w-0">
                        <div class="rp-detail-title"><i class="ti ti-truck me-1"></i>By supplier</div>
                        <div class="table-responsive">
                          <table class="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Supplier</th>
                                <th class="text-end">Qty</th>
                                <th class="text-end">Avg Cost</th>
                                <th class="text-end">Range</th>
                                <th class="text-end">vs Avg</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="supplier in row.suppliers" :key="supplier.supplier_id || 0">
                                <td>
                                  {{ supplier.supplier }}
                                  <span v-if="supplier.cheapest" class="rp-badge success ms-1">Cheapest</span>
                                  <small class="d-block text-muted">
                                    {{ plural(supplier.batches, 'batch') }} · last {{ formatDate(supplier.last_date) }}
                                  </small>
                                </td>
                                <td class="text-end">{{ formatQty(supplier.qty) }}</td>
                                <td class="text-end fw-semibold">{{ formatMoney(supplier.avg_cost) }}</td>
                                <td class="text-end text-nowrap">
                                  <template v-if="supplier.min_cost === supplier.max_cost">{{ formatMoney(supplier.min_cost) }}</template>
                                  <template v-else>{{ formatMoney(supplier.min_cost) }} – {{ formatMoney(supplier.max_cost) }}</template>
                                </td>
                                <td class="text-end" :class="Number(supplier.vs_avg) > 0 ? 'text-danger' : Number(supplier.vs_avg) < 0 ? 'text-success' : 'text-muted'">
                                  {{ Number(supplier.vs_avg) ? `${Number(supplier.vs_avg) > 0 ? '+' : ''}${supplier.vs_avg}%` : '—' }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-if="row.selling_price" class="rp-note mt-2">
                          Selling price {{ formatMoney(row.selling_price) }} · margin at the latest cost
                          <strong :class="Number(row.margin_at_last) < 0 ? 'text-danger' : ''">{{ row.margin_at_last }}%</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-scale"></i>
                  <div class="fw-semibold">No purchases to compare</div>
                  <small class="text-muted">Try a longer period, another supplier or filter.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th class="text-end">Total ({{ plural(rows.length, 'product') }})</th>
              <th class="text-center">{{ formatInt(summary.batch_count) }}</th>
              <th colspan="4"></th>
              <th class="text-end">{{ formatMoney(summary.avg_cost) }}</th>
              <th></th>
              <th class="text-end text-success">{{ Number(summary.saving) ? formatMoney(summary.saving) : '' }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Unit cost = batch cost ÷ quantity received. Change compares the latest batch with the first one in the period.
      Saving is what the quantity would have cost at the cheapest supplier's average price. The supplier index is a
      supplier's spend against the average price of the same items: below 100 means cheaper.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { localToday } from '../inventory/inventoryReportMixin';
import periodReport from '../inventory/periodReportMixin';
import salesReport from '../sales/salesReportMixin';
import purchaseReport from './purchaseReportMixin';

const EMPTY_SUMMARY = {
  product_count: 0, batch_count: 0, qty: 0, cost: 0, avg_cost: 0, rising_count: 0, falling_count: 0, saving: 0,
  biggest_rises: [], biggest_drops: [], supplier_index: [], show_counts: { all: 0, up: 0, down: 0, multi_supplier: 0 },
};
const TREND_TONES = { up: 'danger', down: 'success', flat: 'muted', single: 'muted' };
const TREND_ICONS = { up: 'ti-arrow-up-right', down: 'ti-arrow-down-right', flat: 'ti-arrow-right', single: 'ti-point' };

/** This year up to today: costs need several batches to compare. */
function yearPeriod(today) {
  return { date_from: `${today.slice(0, 4)}-01-01`, date_to: today };
}

export default {
  name: 'PurchaseCostReport',
  mixins: [inventoryReport, periodReport, salesReport, purchaseReport],

  reportUrl: apiUrls.purchase_cost_report.get,
  fileName: 'purchase_cost_comparison',
  columns: [
    { key: 'item_name', label: 'Product' },
    { key: 'batch_count', label: 'Batches', class: 'text-center', numeric: true },
    { key: 'first_cost', label: 'First Cost', class: 'text-end', numeric: true },
    { key: 'last_cost', label: 'Last Cost', class: 'text-end', numeric: true },
    { key: 'change_pct', label: 'Change', class: 'text-end', numeric: true },
    { key: 'spread_pct', label: 'Range', class: 'text-end', numeric: true },
    { key: 'avg_cost', label: 'Avg Cost', class: 'text-end', numeric: true },
    { key: 'purchase_price', label: 'List Price', class: 'text-end', numeric: true },
    { key: 'saving', label: 'Best Supplier', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...yearPeriod(localToday()), supplier: null, show: null,
        warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      TREND_TONES,
      TREND_ICONS,
    };
  },

  computed: {
    periodChanged() {
      return this.activeRange !== 'year';
    },

    hasActiveFilters() {
      const { supplier, show } = this.filters;
      return this.periodChanged || this.itemFiltersActive || !!(supplier || show);
    },

    showPills() {
      const counts = this.summary.show_counts;
      return [
        { key: 'all', label: 'All products', count: counts.all },
        { key: 'up', label: 'Cost rising', icon: 'ti-trending-up', count: counts.up },
        { key: 'down', label: 'Cost falling', icon: 'ti-trending-down', count: counts.down },
        { key: 'multi_supplier', label: 'Several suppliers', icon: 'ti-users', count: counts.multi_supplier },
      ];
    },

    indexBars() {
      const max = Math.max(100, ...this.summary.supplier_index.map((s) => Number(s.index) || 0));
      return this.summary.supplier_index.slice(0, 6).map((s) => ({
        ...s, percent: s.index === null ? 0 : Math.max(4, Math.round(((Number(s.index) || 0) / max) * 100)),
      }));
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.show'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { show } = this.filters;
      return { ...this.periodParams(), ...this.supplierParams(), ...(show ? { show } : {}) };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...yearPeriod(this.today), supplier: null, show: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    setShow(key) {
      this.filters.show = key === 'all' ? null : key;
    },

    toggleShow(key) {
      this.filters.show = this.filters.show === key ? null : key;
    },

    variantParts(row) {
      return row.variant ? row.variant.split(' · ') : [];
    },

    indexClass(index) {
      if (index === null) return 'text-muted';
      return Number(index) > 100.05 ? 'text-danger' : Number(index) < 99.95 ? 'text-success' : '';
    },

    indexTone(index) {
      if (index === null) return '';
      return Number(index) > 100.05 ? 'danger' : Number(index) < 99.95 ? 'success' : 'info';
    },

    diffClass(cost, avg) {
      const diff = (Number(cost) || 0) - (Number(avg) || 0);
      return Math.abs(diff) < 0.005 ? 'text-muted' : diff > 0 ? 'text-danger' : 'text-success';
    },

    diffText(cost, avg) {
      const a = Number(avg) || 0;
      const diff = (Number(cost) || 0) - a;
      if (!a || Math.abs(diff) < 0.005) return '—';
      return `${diff > 0 ? '+' : '−'}${Math.abs(Math.round((diff / a) * 1000) / 10)}%`;
    },

    printReport() {
      const s = this.summary;
      const show = this.showPills.find((p) => p.key === (this.filters.show || 'all'))?.label;
      this.printColumns(
        'Purchase Cost Comparison',
        [this.periodText(), `Showing: ${show}`, this.supplierText(), ...this.itemFiltersText()],
        [
          ['Products', `${this.formatInt(s.product_count)} · ${this.plural(s.batch_count, 'batch')}`],
          ['Average Cost', `${this.formatMoney(s.avg_cost)} on ${this.formatMoney(s.cost)}`],
          ['Rising · Falling', `${this.formatInt(s.rising_count)} · ${this.formatInt(s.falling_count)}`],
          ['Possible Saving', this.formatMoney(s.saving)],
        ],
        [
          { label: 'Item Code', value: (r) => r.item_code },
          { label: 'Product', value: (r) => `${r.item_name}${r.variant ? ` (${r.variant})` : ''}` },
          { label: 'Batches', value: (r) => r.batch_count, num: true },
          { label: 'Suppliers', value: (r) => r.supplier_count, num: true },
          { label: 'First Cost', value: (r) => this.formatMoney(r.first_cost), num: true },
          { label: 'Last Cost', value: (r) => this.formatMoney(r.last_cost), num: true },
          { label: 'Change %', value: (r) => `${r.change_pct}%`, num: true },
          { label: 'Low', value: (r) => this.formatMoney(r.min_cost), num: true },
          { label: 'High', value: (r) => this.formatMoney(r.max_cost), num: true },
          { label: 'Avg Cost', value: (r) => this.formatMoney(r.avg_cost), num: true },
          { label: 'Best Supplier', value: (r) => r.best_supplier },
          { label: 'Saving', value: (r) => this.formatMoney(r.saving), num: true },
        ],
        this.visibleRows,
        { Product: 'Total', 'Avg Cost': this.formatMoney(s.avg_cost), Saving: this.formatMoney(s.saving) },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.pc-detail {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 1.25rem;
}

.pc-best {
  max-width: 160px;
  margin-left: auto;
}

@media (max-width: 1199px) {
  .pc-detail {
    grid-template-columns: 1fr;
  }
}
</style>
